import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { projects } from '../../data/projects';

const Projects = () => (
  <Box component="section" id="projects" sx={{ py: { xs: 12, md: 16 }, backgroundColor: 'background.paper' }}>
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Chip 
          label="Our Portfolio" 
          color="secondary" 
          size="small" 
          sx={{ mb: 2, fontWeight: 600 }}
        />
        <Typography variant="h2" sx={{ 
          mb: 3, 
          color: 'primary.main',
          fontWeight: 700,
          fontSize: { xs: '2rem', md: '3rem' },
          letterSpacing: '-0.02em'
        }}>
          Proven Project Excellence
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: 800, 
          mx: 'auto',
          fontSize: '1.125rem',
          color: 'text.secondary',
          lineHeight: 1.7
        }}>
          Representative projects demonstrating our ability to deliver mission-critical mechanical upgrades with tight
          schedules, complex logistics, and strict compliance requirements across diverse sectors.
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        {projects.map((project, index) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={project.name}>
            <Fade in={true} timeout={600 + (index * 100)}>
              <Card sx={{ 
                height: '100%',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: (theme) => theme.shadows[16],
                },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,246,248,0.9) 100%)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <Chip 
                      label={project.sector}
                      color="secondary"
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        mb: 2,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    />
                    <Typography variant="h5" sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 2,
                      fontSize: '1.375rem'
                    }}>
                      {project.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {project.year || 'Recent'}
                        </Typography>
                      </Box>
                      {project.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {project.location}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  <List dense sx={{ mb: 3 }}>
                    {project.highlights.map((highlight, idx) => (
                      <ListItem key={idx} sx={{ pl: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon color="secondary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={highlight}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            lineHeight: 1.6
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>
      
      {/* Removed portfolio CTA per content cleanup requirements */}
    </Container>
  </Box>
);

export default Projects;
